// Local-only persistence for "My reports / experiments" in the right panel.
// This is intentionally small + resilient (handles corrupt JSON and storage limits).

const NS = 'dd:workspace:v1';

export function getAuthUserKey(user) {
  if (!user) return 'anon';
  return (
    user.id ||
    user.user_id ||
    user.uid ||
    user.email ||
    user.username ||
    user.name ||
    user.full_name ||
    'user'
  );
}

function keyFor(userKey) {
  return `${NS}:${String(userKey || 'anon')}`;
}

function safeParse(json, fallback) {
  try { return JSON.parse(json); } catch { return fallback; }
}

function nowISO() {
  return new Date().toISOString();
}

export function readWorkspace(userKey) {
  if (typeof window === 'undefined') return { version: 1, modules: {} };
  const raw = window.localStorage.getItem(keyFor(userKey));
  const data = safeParse(raw, null);
  if (!data || typeof data !== 'object') return { version: 1, modules: {} };
  if (!data.modules || typeof data.modules !== 'object') data.modules = {};
  if (!data.version) data.version = 1;
  return data;
}

export function writeWorkspace(userKey, next) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(keyFor(userKey), JSON.stringify(next));
  window.dispatchEvent(new CustomEvent('dd-storage', { detail: { ns: NS, userKey } }));
}

function upsertFront(list, item, max = 25) {
  const out = [item, ...(Array.isArray(list) ? list : []).filter(x => x && x.id !== item.id)];
  return out.slice(0, max);
}

export function addWorkspaceItem(userKey, moduleId, item) {
  const ws = readWorkspace(userKey);
  const mod = ws.modules[moduleId] || { items: [] };
  const nextItem = {
    id: item?.id || `${moduleId}_${Date.now()}`,
    title: item?.title || 'Untitled',
    createdAt: item?.createdAt || nowISO(),
    meta: item?.meta || {},
    payload: item?.payload || null,
    kind: item?.kind || 'item',
  };
  const next = {
    ...ws,
    modules: {
      ...ws.modules,
      [moduleId]: {
        ...mod,
        items: upsertFront(mod.items, nextItem),
      },
    },
  };
  writeWorkspace(userKey, next);
  return nextItem;
}

export function removeWorkspaceItem(userKey, moduleId, itemId) {
  const ws = readWorkspace(userKey);
  const mod = ws.modules[moduleId] || { items: [] };
  const next = {
    ...ws,
    modules: {
      ...ws.modules,
      [moduleId]: {
        ...mod,
        items: (mod.items || []).filter(x => x && x.id !== itemId),
      },
    },
  };
  writeWorkspace(userKey, next);
}

// Storage key for ML experiments session state (localStorage, scoped per user + module).
export function expwsKey(userKey, scope = 'machine_learning') {
  return `dd:expws:v1:${String(userKey || 'anon')}:${String(scope || 'all')}`;
}

export function readExpws(userKey, scope) {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(expwsKey(userKey, scope));
  return safeParse(raw, null);
}

export function writeExpws(userKey, scope, data) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(expwsKey(userKey, scope), JSON.stringify(data));
  window.dispatchEvent(new CustomEvent('dd-storage', { detail: { ns: 'dd:expws:v1', userKey, scope } }));
}

